import { useReducer, useEffect, createElement } from 'react';

const identity = (a) => a;
const split = (char) => (text) => text.split(char);
const pipe = (...fns) => fns.reduce((prevFn, nextFn) => (value) => nextFn(prevFn(value)));
const slice = (begin, end) => (arr) => arr.slice(begin, end);
const reverse = (arr) => Array.prototype.slice.call(arr, 0).reverse();
/* Adapted From:
 * https://stackoverflow.com/a/6274381/6402238 */
function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}
const tail = (list) => list[list.length - 1];

const defaultCharset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const defaultOptions = {
    amount: 1,
    sequential: false,
    preserveWhitespace: true,
    preserveCasing: false,
    previousText: undefined,
    characterSet: defaultCharset,
};
function matchCase(char, charToMatch) {
    if (/[A-Z]/.test(charToMatch))
        return char.toUpperCase();
    if (/[a-z]/.test(charToMatch))
        return char.toLowerCase();
    return char;
}
function randomFromArray(arr) {
    const index = Math.floor(Math.random() * arr.length);
    return arr[index];
}
const scrambleCharWithCharSet = (charset, preserveCasing) => {
    function scrambleChar(char) {
        if (char.length > 1)
            throw new Error(`You can only scramble one character at a time. Received: ${char}`);
        const randomChar = randomFromArray(charset);
        const casedChar = preserveCasing ? matchCase(randomChar, char) : randomChar;
        if (charset.length === 1)
            return casedChar;
        /* If there is more than one possible character, do not return
         * one that matches the input. Instead, call this function again */
        return casedChar === char ? scrambleChar(char) : casedChar;
    }
    return scrambleChar;
};
const getIndices = (arr) => arr.map((_, index) => index);
const scramble = (text, userOptions) => {
    const config = Object.assign(Object.assign({}, defaultOptions), userOptions);
    const charSet = config.characterSet.split('');
    const scrambleChar = scrambleCharWithCharSet(charSet, config.preserveCasing);
    const scrambleLimit = Math.round(config.amount * text.length);
    const characterIsUnscrambled = (index) => config.previousText.charAt(index) === text.charAt(index);
    const charactersToScramble = pipe(split(''), getIndices, reverse, config.sequential ? identity : shuffle, slice(0, scrambleLimit))(text);
    return text
        .split('')
        .map((char, index) => {
        if (config.preserveWhitespace && /\s/.test(char))
            return char;
        if (config.previousText && characterIsUnscrambled(index))
            return char;
        if (!charactersToScramble.includes(index))
            return char;
        return scrambleChar(char);
    })
        .join('');
};

const sequence = (text, steps, config) => {
    const counter = Array(steps).fill(undefined);
    return counter.reduce((acc, _, index) => {
        const previousText = tail(acc);
        const amount = 1 - index / (steps - 1);
        const newText = scramble(text, Object.assign(Object.assign({}, config), { amount,
            previousText }));
        return [...acc, newText];
    }, []);
};

const START = 'START';
const STOP = 'STOP';
const RESTART = 'RESTART';
const TICK = 'TICK';
const reducer = (state, action) => {
    switch (action.type) {
        case RESTART:
        case START:
            return Object.assign(Object.assign({}, state), { startTime: new Date().getTime() });
        case STOP:
            return Object.assign(Object.assign({}, state), { previouslyElapsed: state.elapsed, startTime: undefined });
        case TICK:
            return Object.assign(Object.assign({}, state), { elapsed: action.elapsed });
        default:
            return state;
    }
};
const defaultConfig = {
    interval: 30,
};
const useStopwatch = (running, userConfig) => {
    const config = Object.assign(Object.assign({}, defaultConfig), userConfig);
    const { interval } = config;
    const [state, dispatch] = useReducer(reducer, {
        startTime: undefined,
        elapsed: 0,
        previouslyElapsed: 0,
    });
    const { elapsed, startTime } = state;
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newElapsed = new Date().getTime() - state.startTime + state.previouslyElapsed;
            dispatch({ type: TICK, elapsed: newElapsed });
        }, interval);
        if (!running) {
            dispatch({ type: STOP });
            clearTimeout(timeoutId);
        }
        if (running && state.startTime === undefined) {
            dispatch({ type: START });
            clearTimeout(timeoutId);
        }
        return () => clearTimeout(timeoutId);
    }, [running, elapsed, startTime]);
    return { elapsed };
};

const TICK$1 = 'TICK';
const reducer$1 = (state, action) => {
    switch (action.type) {
        case TICK$1:
            return Object.assign(Object.assign({}, state), { currentText: action.newText, progress: action.progress });
        default:
            return state;
    }
};
const defaults = {
    running: true,
    interval: 30,
    duration: 3000,
};
const useScrambledText = ({ text, config, running: userRunning, interval: userInterval, duration: userDuration, }) => {
    const duration = userDuration || defaults.duration;
    const running = userRunning !== undefined ? userRunning : defaults.running;
    const interval = userInterval || defaults.interval;
    const { elapsed } = useStopwatch(running, { interval });
    const [state, dispatch] = useReducer(reducer$1, {
        currentText: scramble(text, config),
        progress: 0,
    });
    useEffect(() => {
        if (elapsed === 0)
            return;
        if (config && config.amount !== undefined)
            return;
        const progress = elapsed / duration;
        const newText = scramble(text, Object.assign(Object.assign({}, config), { amount: 1 - progress, previousText: state.currentText }));
        dispatch({ type: TICK$1, progress, newText });
    }, [elapsed]);
    // useEffect(() => {
    //   /** Don't refresh with new values if running is false,
    //    * or if the user is supplying their own 'amount' for the config */
    //   if (running === false) return () => undefined
    //   if (config && config.amount !== undefined) return () => undefined
    //   const timeoutId = setTimeout(() => {
    //     const elapsed = new Date().getTime() - initialTime
    //     const newProgress =
    //       1 - Math.min(1, elapsed / (duration || defaults.duration))
    //     const newText = scramble(text, {
    //       ...config,
    //       amount: newProgress,
    //       previousText: currentText,
    //     })
    //     dispatch({ type: TICK, newText, progress: newProgress })
    //   }, interval || defaults.interval)
    //   return () => clearTimeout(timeoutId)
    // }, [currentText, progress, initialTime])
    return Object.assign(Object.assign({}, state), { elapsed });
};

const ScrambledText = ({ text, config, running, interval, duration, wrapper, }) => {
    const { currentText } = useScrambledText({
        text,
        config,
        running,
        interval,
        duration,
    });
    const Tag = wrapper || 'span';
    return createElement(Tag, null, currentText);
};

export { ScrambledText, scramble, sequence, useScrambledText };
